import type { IButtonProps } from 'atoms/Button'
import type { IActionButtonProps } from './ActionButton'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { IconSC } from 'atoms/Icon'
import { TextBoxSC } from 'atoms/TextBox'
import { BORDERS, CORNERS, FIXED_INDENTS, MEDIA, TRANSITIONS } from 'vars/index'
import {
  ButtonBordersWidth,
  ButtonCorners,
  ButtonFluid,
} from './ActionButton.h'

export const ActionButtonSC = styled(ButtonSC)<
  IButtonProps & IActionButtonProps
>`
  position: relative;
  display: inline-flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  max-width: 100%;
  border: none;
  min-width: ${({ icon, title }) => (title ? (icon ? 164 : 132) : 0)}px;
  border-radius: ${({ corners }) =>
    `var(${CORNERS[corners || ButtonCorners.CORNERS_0]})`};

  outline: none;
  cursor: pointer;
  overflow: hidden;
  transition: all var(${TRANSITIONS.LINEAR_400});
  font: ${({ font }) => `var(${font})`};
  height: ${({ fluid }) =>
    fluid === ButtonFluid.Both || fluid === ButtonFluid.Vertical
      ? '100%'
      : 'auto'};
  width: ${({ fluid }) =>
    fluid === ButtonFluid.Horizontal || fluid === ButtonFluid.Both
      ? '100%'
      : 'auto'};

  padding: ${({ paddings }) =>
    paddings?.length
      ? paddings.map((padding) => `var(${padding})`).join(' ')
      : 0};

  background-color: ${({
    backgroundColorDefault,
    selected,
    backgroundColorSelectedDefault,
  }) =>
    `var(${
      selected ? backgroundColorSelectedDefault : backgroundColorDefault
    })`};
  color: ${({ colorDefault, colorSelectedDefault, selected }) =>
    `var(${selected ? colorSelectedDefault : colorDefault})`};
  box-shadow: ${({
    borderWidth,
    bordersColorDefault,
    selected,
    bordersColorSelectedDefault,
  }) =>
    `inset 0 0 0 var(${
      BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
    }) var(${selected ? bordersColorSelectedDefault : bordersColorDefault}) `};

  ${MEDIA.NO_TOUCH} {
    &:hover {
      /* Hover is painted onto the element itself, not stacked as a state
         layer. Templates matching Figma's 10%+10% white should pass
         SURFACE_OPACITY_LIGHT_20 as backgroundColorHovered. */
      background-color: ${({
        backgroundColorHovered,
        backgroundColorSelectedHovered,
        selected,
      }) =>
        `var(${
          selected ? backgroundColorSelectedHovered : backgroundColorHovered
        })`};
      color: ${({ colorHovered, colorSelectedHovered, selected }) =>
        `var(${selected ? colorSelectedHovered : colorHovered})`};
      box-shadow: ${({
        borderWidth,
        bordersColorHovered,
        bordersColorSelectedHovered,
        selected,
      }) =>
        `inset 0 0 0 var(${
          BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
        }) var(${
          selected ? bordersColorSelectedHovered : bordersColorHovered
        })`};
    }
  }

  &:active {
    background-color: ${({
      backgroundColorPressed,
      backgroundColorSelectedPressed,
      selected,
    }) =>
      `var(${
        selected ? backgroundColorSelectedPressed : backgroundColorPressed
      })`};
    color: ${({ colorPressed, selected, colorSelectedPressed }) =>
      `var(${selected ? colorSelectedPressed : colorPressed})`};
    box-shadow: ${({
      borderWidth,
      bordersColorPressed,
      selected,
      bordersColorSelectedPressed,
    }) =>
      `inset 0 0 0 var(${
        BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
      }) var(${selected ? bordersColorSelectedPressed : bordersColorPressed})`};
  }

  &:disabled {
    background-color: ${({ backgroundColorDisabled }) =>
      `var(${backgroundColorDisabled})`};
    color: ${({ colorDisabled }) => `var(${colorDisabled})`};
    box-shadow: ${({ borderWidth, bordersColorDisabled }) =>
      `inset 0 0 0 var(${
        BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
      }) ${bordersColorDisabled}`};
  }

  &:before {
    content: '';
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background-image: ${({ gradient, disabled, selected }) =>
      selected && disabled ? 'none' : selected ? `var(${gradient})` : 'none'};
    opacity: 1;
    transition: opacity var(${TRANSITIONS.LINEAR_400});
    z-index: 0;
  }

  ${MEDIA.NO_TOUCH} {
    &:hover:before {
      opacity: 0;
    }
  }

  ${TextBoxSC} {
    color: inherit;
    z-index: 1;
    order: 1;
  }

  ${IconSC} {
    z-index: 1;
    color: inherit;
    order: ${({ iconOrder }) => iconOrder || 1};
    margin: ${({ iconOrder }) =>
      iconOrder === 2
        ? `0 0 0 var(${FIXED_INDENTS.INDENT_20})`
        : `0 var(${FIXED_INDENTS.INDENT_20}) 0 0`};

    &:only-child {
      margin: 0;
    }
  }
`
